import React from 'react';
import {
  CheckIcon,
  ArrowRightIcon,
  MinusIcon,
  PlusIcon,
  ArrowCounterClockwiseIcon,
  XCircleIcon,
  CheckCircleIcon,
} from '@phosphor-icons/react';

export type ChangeStatus =
  | 'unchanged'
  | 'modified'
  | 'removed'
  | 'added'
  | 'new'
  | 'manual'
  | 'restored'
  | 'deleted';

export interface StatusStyleConfig {
  border: string;
  bg: string;
  iconBg: string;
  textColor: string;
  icon: React.ReactNode | null;
}

export function getStatusStyle(status: ChangeStatus): StatusStyleConfig {
  const configs: Record<ChangeStatus, StatusStyleConfig> = {
    unchanged: {
      border: 'border-slate-200',
      bg: 'bg-slate-50',
      iconBg: 'bg-slate-100',
      textColor: 'text-slate-600',
      icon: <CheckCircleIcon className="h-3.5 w-3.5 text-slate-600" />, // No icon - "unchanged" doesn't mean "valid"
    },
    modified: {
      border: 'border-blue-200',
      bg: 'bg-blue-50',
      iconBg: 'bg-blue-100',
      textColor: 'text-blue-700',
      icon: <ArrowRightIcon className="h-3.5 w-3.5 text-blue-700" />,
    },
    removed: {
      border: 'border-slate-300',
      bg: 'bg-slate-100',
      iconBg: 'bg-slate-200',
      textColor: 'text-slate-500',
      icon: <MinusIcon className="h-3.5 w-3.5 text-slate-500" />,
    },
    added: {
      border: 'border-yellow-200',
      bg: 'bg-yellow-50',
      iconBg: 'bg-yellow-100',
      textColor: 'text-yellow-700',
      icon: <PlusIcon className="h-3.5 w-3.5 text-yellow-700" />,
    },
    new: {
      border: 'border-yellow-200',
      bg: 'bg-yellow-50',
      iconBg: 'bg-yellow-100',
      textColor: 'text-yellow-700',
      icon: <PlusIcon className="h-3.5 w-3.5 text-yellow-700" />,
    },
    manual: {
      border: 'border-orange-200',
      bg: 'bg-orange-50',
      iconBg: 'bg-orange-100',
      textColor: 'text-orange-700',
      icon: <PlusIcon className="h-3.5 w-3.5 text-orange-700" />,
    },
    restored: {
      border: 'border-green-200',
      bg: 'bg-green-50',
      iconBg: 'bg-green-100',
      textColor: 'text-green-700',
      icon: (
        <ArrowCounterClockwiseIcon className="h-3.5 w-3.5 text-green-700" />
      ),
    },
    deleted: {
      border: 'border-red-200',
      bg: 'bg-red-50',
      iconBg: 'bg-red-100',
      textColor: 'text-red-700',
      icon: <XCircleIcon className="h-3.5 w-3.5 text-red-700" />,
    },
  };

  return configs[status] || configs.new;
}

export function getStatusLabel(status: ChangeStatus): string {
  const labels: Record<ChangeStatus, string> = {
    unchanged: 'No change',
    modified: 'Modified',
    removed: 'Removed',
    added: 'New',
    new: 'New',
    restored: 'Restored',
    deleted: 'Deleted',
    manual: 'Manually Added',
  };

  return labels[status] || '';
}
